import type { ReactNode } from 'react';

import { SecureImage } from '@/components/ui/SecureImage';
import { ROLE_LABEL } from '@/constants/roles';
import { getNameInitials } from '@/lib/utils';
import type { StaffMember } from '@/types/staff';
import { staffApi } from '../api/staff.api';

/**
 * Who a row is about: face, name, email, staff number, phones and staff type.
 *
 * The staff register's first column, shared with the attendance register so
 * the two always describe a person the same way — add a line here and both
 * tables carry it.
 */

export type StaffIdentityMember = Pick<
  StaffMember,
  'uuid' | 'fullName' | 'email' | 'employeeCode' | 'mobile' | 'staffType' | 'hasPhoto' | 'alternateMobile'
>;

export interface StaffIdentityProps {
  member: StaffIdentityMember;
  /** Beside the name — the attendance register's "You". */
  badge?: ReactNode;
  /** Under the last line, for what only one table adds. */
  footer?: ReactNode;
}

export function StaffIdentity({ member, badge, footer }: StaffIdentityProps) {
  const initials = getNameInitials(member.fullName);

  return (
    <div className="flex min-w-0 items-start gap-2.5">
      <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-100 flex items-center justify-center font-black text-xs shrink-0 overflow-hidden border border-slate-300 dark:border-slate-600 shadow-xs mt-0.5">
        {member.hasPhoto ? (
          <SecureImage
            path={staffApi.photoPath(member.uuid)}
            alt={member.fullName}
            className="w-full h-full object-cover"
            fallback={initials}
          />
        ) : (
          initials
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 font-extrabold text-slate-900 dark:text-white text-sm">
          <span className="truncate">{member.fullName}</span>
          {badge}
        </div>
        <div className="text-sm font-medium text-slate-500 dark:text-slate-400 truncate">{member.email}</div>
        <div className="text-sm font-mono font-bold text-slate-700 dark:text-slate-300 mt-0.5 truncate">
          {member.employeeCode ?? '—'}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 font-medium truncate">
          {member.mobile ? `+91 ${member.mobile}` : 'No phone number'}
        </div>
        {member.alternateMobile && (
          <div className="text-sm text-slate-500 dark:text-slate-400 font-medium truncate">
            Alt: {member.alternateMobile}
          </div>
        )}
        <div className="text-sm font-semibold text-slate-800 dark:text-slate-200 truncate mt-0.5">
          {ROLE_LABEL[member.staffType] || 'Staff'}
        </div>
        {footer}
      </div>
    </div>
  );
}
